import {html, LitElement} from "../libs/lit.dist.js";
import styles from './pk-stepper.css.js';
import './pk-stepper-step.js';

/**
 * Komponent steppera. Wyświetla listę korków i postęp ich realizacji.
 *
 * @see https://www.figma.com/design/0QzwedkPLGpsg7lVkw1db0/Polkurier---prace?node-id=1991-1577&t=krvPsHLwXAZCvLQZ-0

 * @property {Number} step Bieżący krok. Jeśli wartość jest większa od ilości kroków to całość zostanie oznaczona jako `done`
 * @property {Boolean} done Wymusza stan końca niezależnie od bieżącego kroku
 * @property {Boolean} horizontal Wymusza tryb pionowy (nawet w wersji mobilnej)
 * @property {Boolean} vertical Wymusza tryb poziomy
 * @property {Array<String>} steps Tablica kroków do wyświetlenia
 *
 * @example
 * <pk-stepper step="2">
 *     <pk-stepper-step>Potwierdzenie danych</pk-stepper-step>
 *     <pk-stepper-step>Ustawienie hasła</pk-stepper-step>
 *     <pk-stepper-step>Akceptacja umowy</pk-stepper-step>
 * </pk-stepper>
 */
export class PkStepper extends LitElement {

    static styles = styles;

    static properties = {
        step: {type: Number, reflect: true},
        done: {type: Boolean, reflect: true},
        horizontal: {type: Boolean, reflect: true},
        autoHorizontal: {type: Boolean, reflect: true},
        vertical: {type: Boolean, reflect: true},
        steps: {type: Array},
    }

    #mediaQuery;

    get #slot() {
        return this.renderRoot.querySelector('slot');
    }

    get #slottedStepsElements() {
        const elements = [];
        for (let element of this.#slot.assignedElements()) {
            if (element.tagName === 'PK-STEPPER-STEP') {
                elements.push(element);
            }
        }
        return elements;
    }

    get #stepsCount() {
        return this.steps.length + this.#slottedStepsElements.length;
    }

    constructor() {
        super();
        this.step = 1;
        this.steps = [];
    }

    #handleMediaQueryChange() {
        if (!this.vertical) {
            this.autoHorizontal = this.#mediaQuery.matches;
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.#mediaQuery = window.matchMedia("(max-width: 768px)");
        this.#mediaQuery.addEventListener('change', () => {
            this.#handleMediaQueryChange();
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#mediaQuery.removeEventListener('change');
        this.#mediaQuery = undefined;
    }

    firstUpdated(t) {
        super.firstUpdated(t);
        this.#handleMediaQueryChange();
        this.#enumerateSteps();
        this.#slot?.addEventListener('slotchange', () => {
            this.#updateDone();
            this.#enumerateSteps();
        });
    }

    #updateDone() {
        this.done = this.step > this.#stepsCount;
    }

    #enumerateSteps() {
        const steps = this.#slottedStepsElements;
        let i = (this.steps?.length ?? 0) + 1;
        for (let step of steps) {
            step.step = i++;
            step.done = this.#isDone(step.step);
            step.current = this.#isCurrent(step.step);
            step.horizontal = (this.horizontal || this.autoHorizontal) && !this.vertical;
        }
    }

    #isDone(step) {
        return step < this.step || this.done;
    }

    #isCurrent(step) {
        return step === this.step && !this.done;
    }

    update(t) {
        super.update(t);

        if (t.has('step') && t.get('step') > this.step) {
            this.setAttribute('direction', 'reverse');
        } else {
            this.removeAttribute('direction');
        }

        if (t.has('step') || t.has('done') || t.has('horizontal') || t.has('autoHorizontal') || t.has('vertical')) {
            this.#enumerateSteps();
        }

        if (t.has('step') || t.has('steps')) {
            this.#updateDone();
        }
    }

    render() {
        return html`
            ${this.steps?.map((step, i) => html`
                <pk-stepper-step
                    .step="${i + 1}"
                    .done="${this.#isDone(i + 1)}"
                    .current="${this.#isCurrent(i + 1)}"
                    .horizontal="${this.horizontal || this.autoHorizontal && !this.vertical}"
                >${step}
                </pk-stepper-step>
            `)}
            <slot></slot>
        `;
    }
}

if (!customElements.get('pk-stepper')) {
    customElements.define('pk-stepper', PkStepper);
}
